import type { ExploreCatalogSummary, ExploreCourseData, MeetingTime } from '@/app/types/interfaces';
import { getCampusColor } from '@/app/types/colors';

const unavailable = /^(tba|staff|unknown|to be announced)$/i;

/** Return a campus accent only when every supplied record identifies one known campus. */
export function getSingleCampusAccent(course: Pick<ExploreCourseData, 'campus' | 'campuses' | 'sections'>): string | null {
  const raw = course.campuses?.length
    ? course.campuses
    : course.sections.length
      ? course.sections.map((section) => section.campus)
      : [course.campus];
  const normalized = raw.map((value) => value?.trim() ?? '');
  if (!normalized.length || normalized.some((value) => !value || /^unknown$/i.test(value))) return null;
  const campuses = [...new Set(normalized)];
  if (campuses.length !== 1) return null;

  const campus = campuses[0];
  const key = campus.toLocaleLowerCase().replace(/[^a-z]/g, '');
  const canonical: Record<string, string> = {
    busch: 'Busch', collegeave: 'College Avenue', collegeavenue: 'College Avenue',
    cookdoug: 'Cook/Douglass', cookdouglass: 'Cook/Douglass', cd: 'Cook/Douglass',
    livingston: 'Livingston', online: 'Online', web: 'Online', remote: 'Remote',
    newark: 'Newark', camden: 'Camden', downtownnewbrunswick: 'Downtown New Brunswick',
    nb: 'NB', offcampus: 'Off Campus', studyabroad: 'Study Abroad',
  };
  const known = canonical[key];
  return known ? getCampusColor(known, 'solid') : null;
}

export function usefulInstructorNames(course: Pick<ExploreCourseData, 'instructors' | 'sections'>): string[] {
  const names = course.instructors?.length ? course.instructors : course.sections.flatMap((section) => section.instructors || []);
  const seen = new Set<string>();
  return names.flatMap((rawName) => {
    const name = rawName.trim().replace(/\s+/g, ' ');
    const key = name.toLocaleLowerCase();
    if (!name || unavailable.test(name) || seen.has(key)) return [];
    seen.add(key);
    return [name];
  });
}

export function formatMinutes(total: number): string {
  const hour = Math.floor(total / 60);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${String(total % 60).padStart(2, '0')} ${suffix}`;
}

export function formatMeeting(meeting: MeetingTime): string {
  const day = meeting.day && !unavailable.test(meeting.day) ? meeting.day : null;
  const time = meeting.startMinute > 0 && meeting.endMinute > meeting.startMinute
    ? `${formatMinutes(meeting.startMinute)}–${formatMinutes(meeting.endMinute)}`
    : null;
  const mode = meeting.mode && !unavailable.test(meeting.mode) ? meeting.mode : null;
  const location = meeting.location && !unavailable.test(meeting.location) ? meeting.location : null;
  return [day, time, mode, location].filter(Boolean).join(' · ') || 'Meeting details unavailable';
}

export function scheduleSignature(section: Pick<ExploreCourseData['sections'][number], 'campus' | 'meetingTimes'>): string {
  return `${section.campus}|${section.meetingTimes.map((meeting) => [meeting.day, meeting.startMinute, meeting.endMinute, meeting.mode, meeting.location].join(':')).join(';')}`;
}

export function createSchedulePatterns(sections: ExploreCourseData['sections'], limit = 3): ExploreCatalogSummary['schedulePatterns'] {
  const seen = new Set<string>();
  return sections.flatMap((section) => {
    const signature = scheduleSignature(section);
    if (seen.has(signature) || seen.size >= limit) return [];
    seen.add(signature);
    return [{ section: section.section, campus: section.campus, meetingTimes: section.meetingTimes, isOpen: section.isOpen }];
  });
}

export function shouldAdoptUrlQuery(draft: string, urlQuery: string, activelyEditing: boolean): boolean {
  return !activelyEditing && draft !== urlQuery;
}
